/**
 * The rules the password form is under, with no React and no DOM in them.
 *
 * They live here rather than inside the component for the reason the rest of
 * `lib/` exists: what makes the button light up, and when a mismatch is worth
 * saying out loud, is the part worth testing, and testing it through a rendered
 * form would only be testing React.
 */

/** Mirrors MIN_PASSWORD_LENGTH in server/src/accounts.ts. */
export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordDraft {
  /** Empty on the screen that does not ask for it — see `asks` below. */
  current: string;
  next: string;
  repeat: string;
  /**
   * Whether the current password is part of this form. False on the screen that
   * meets somebody who has just typed the issued password at the login form:
   * there is nothing there for them to prove twice.
   */
  asks: boolean;
}

export interface PasswordChecks {
  canSubmit: boolean;
  /** Something has been typed, and it is not long enough yet. */
  tooShort: boolean;
  /** The repeat has text in it and disagrees with the new password. */
  mismatch: boolean;
  /** The line under the new password: the rule, or the complaint about it. */
  hint: string;
}

/**
 * Both complaints are held back until there is something to complain about, and
 * that is the whole of the design here. «Слишком короткий пароль» on the first
 * keystroke of an eight-character password is true and useless, and «Пароли не
 * совпадают» under an empty repeat field says somebody got it wrong before they
 * have had a chance to type it.
 */
export function passwordChecks(draft: PasswordDraft): PasswordChecks {
  const tooShort = draft.next.length > 0 && draft.next.length < MIN_PASSWORD_LENGTH;
  const mismatch = draft.repeat.length > 0 && draft.repeat !== draft.next;
  const canSubmit =
    (!draft.asks || draft.current.length > 0) &&
    draft.next.length >= MIN_PASSWORD_LENGTH &&
    draft.repeat === draft.next;

  return {
    canSubmit,
    tooShort,
    mismatch,
    hint: tooShort ? 'Слишком короткий пароль' : `Не короче ${MIN_PASSWORD_LENGTH} символов`,
  };
}
